import { useSearchParamsState } from 'hooks/useSearchParamState';
import { useStore } from 'hooks/useStore';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export const useConstraintForecast = () => {
  const {
    UserStore: { userModulesRoles },
    ConstrainedForecastViewStore: {
      contractDetails,
      conceptDetails,
      isConceptLoading,
      directcurrentNDCIndex,
      indirectcurrentNDCIndex,
      directNDCheading,
      indirectNDCheading,
      constrainedForecastData,
      isConstrainedForecastLoading,
      isInvoiceSalesLoading,
      invoiceSalesSummary,
      isConstrainedForecastNotesDataLoading,
      isConceptDetailsLoading,
      conceptSearchInput,
      constrainedForecastStatus,
      isConstrainedForecastStatusLoading,
      constrainedForecastPlanningPeriods,
      constrainedForecastPeriod,
      constrainedFOrecastPlanningPeriodLoading,
      forecastBtnLabel,
      handleGetConceptDetails,
      setDirectCurrentTabIndex,
      fetchForecastGFCNotes,
      setIndirectCurrentTabIndex,
      setConstrainedForecastPeriod,
      ConstrainedForecastNotesData,
      fetchBulkUpdateNDCs,
      fetchForecastProgress,
    },
  } = useStore();
  const [_gfcParam, setGfcParam] = useSearchParamsState('gfc', '');
  const [isDirectMode, setIsDirectMode] = useState<boolean>(true);
  const [searchParams] = useSearchParams();
  const [ndcParam, _setNdcParam] = useSearchParamsState('ndc', '');
  const searchValue = searchParams.get('gfc');

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    fetchForecastGFCNotes(
      isDirectMode
        ? directNDCheading[directcurrentNDCIndex]?.ndc
        : indirectNDCheading[indirectcurrentNDCIndex]?.ndc,
      signal
    );

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directcurrentNDCIndex, indirectcurrentNDCIndex, isDirectMode]);

  useEffect(() => {
    if (searchValue) {
      handleGetConceptDetails(parseFloat(searchValue));
    }
     
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // The HOC (and its search bar) stays mounted when navigating edit → view, so the
  // bulk update To-Do/Completed list and progress must be refreshed here to reflect saves.
  useEffect(() => {
    fetchBulkUpdateNDCs();
    fetchForecastProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // get 15 months starting from the month after the selected planning period
  // (falls back to today while no plan is selected)
  const getFutureViewMonths = () => {
    const arr: { label: string; dateString: string }[] = [];
    let startMonth: number; // 0-indexed month of the planning period
    let startYear: number;
    if (constrainedForecastPeriod?.code) {
      const [year, month] = constrainedForecastPeriod.code.split('-');
      startMonth = Number(month) - 1;
      startYear = Number(year);
    } else {
      const dt = new Date();
      startMonth = dt.getMonth();
      startYear = dt.getFullYear();
    }
    const monthShortHand = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    for (let i = 0; i < 15; i++) {
      const month = (startMonth + i + 1) % 12;
      const year = startYear + Math.floor((startMonth + i + 1) / 12);
      arr.push({
        label: `${monthShortHand[month]}-${year}`,
        dateString: `${year}-${month >= 9 ? month + 1 : `0${month + 1}`}-01`,
      });
    }

    return arr;
  };

  const forecastingMonthsView: { label: string; dateString: string }[] = getFutureViewMonths();

  const handleDirectNDCTabChange = (e: any) => {
    setDirectCurrentTabIndex(e.index);
  };

  const handleIndirectNDCTabChange = (e: any) => {
    setIndirectCurrentTabIndex(e.index);
  };

  // utility function to get sales details
  const getInvoiceSalesDetails = (ndc: string, want: string) => {
    if (invoiceSalesSummary.length === 0) return;

    const ndcSummary = invoiceSalesSummary.filter(item => item.NDC11 === ndc);

    if (ndcSummary.length === 0) {
      return 'NA';
    } else {
      if (want === 'ytd' && ndcSummary[0].YTDAverage) {
        return ndcSummary[0].YTDAverage;
      } else if (want === '3ma' && ndcSummary[0].ThreeMonthAverage) {
        return ndcSummary[0].ThreeMonthAverage;
      } else if (want === 'pm' && ndcSummary[0].LastMonthAverage) {
        return ndcSummary[0].LastMonthAverage;
      } else {
        return 'NA';
      }
    }
  };

  const getUnconstrainedForecast = (ndc: string, forecastDate: string) => {
    if (isConstrainedForecastLoading || constrainedForecastData.forecasts.length === 0) {
      return;
    }

    const ndcForecast = constrainedForecastData.forecasts.filter(item => item.NDC11 === ndc)[0];
    if (!ndcForecast) return '-';

    const forecast = ndcForecast.forecast_details.filter(
      item => item.ForecastPeriod === forecastDate
    );

    if (forecast && forecast.length > 0) {
      const val = forecast[0]?.UnconstrainedForecast;
      return typeof val === 'number' ? val.toLocaleString('en-US') : val;
    }

    // Copy-forward: no data for this month — return the last available month's value
    const sorted = [...ndcForecast.forecast_details].sort((a, b) =>
      a.ForecastPeriod < b.ForecastPeriod ? -1 : 1
    );
    if (sorted.length === 0) return '-';
    const val = sorted[sorted.length - 1].UnconstrainedForecast;
    return typeof val === 'number' ? val.toLocaleString('en-US') : val;
  };

  const getConstrainedForecast = (ndc: string, forecastDate: string) => {
    if (constrainedForecastData.forecasts.length === 0) {
      return;
    }

    const ndcForecast = constrainedForecastData.forecasts.filter(item => item.NDC11 === ndc)[0];
    if (!ndcForecast) return '-';

    const forecast = ndcForecast.forecast_details.filter(
      item => item.ForecastPeriod === forecastDate
    );

    if (forecast && forecast.length > 0) {
      const val = forecast[0]?.ConstrainedForecast;
      return typeof val === 'number' ? val.toLocaleString('en-US') : val;
    }

    // Copy-forward: no data for this month — return the last available month's value
    const sorted = [...ndcForecast.forecast_details].sort((a, b) =>
      a.ForecastPeriod < b.ForecastPeriod ? -1 : 1
    );
    if (sorted.length === 0) return '-';
    const val = sorted[sorted.length - 1].ConstrainedForecast;
    return typeof val === 'number' ? val.toLocaleString('en-US') : val;
  };

  const handleOptionChange = (option: { label: string; code: string }) => {
    setConstrainedForecastPeriod(option);
  };

  useEffect(() => {
    if (conceptDetails && conceptDetails.gfc) setGfcParam(conceptDetails.gfc.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conceptDetails]);

  useEffect(() => {
    if (directNDCheading.length > 0 && ndcParam) {
      for (let i = 0; i < directNDCheading.length; i++) {
        if (directNDCheading[i].ndc === ndcParam) {
          setDirectCurrentTabIndex(i);
          setIsDirectMode(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directNDCheading, ndcParam]);

  useEffect(() => {
    if (indirectNDCheading.length > 0 && ndcParam) {
      for (let i = 0; i < indirectNDCheading.length; i++) {
        if (indirectNDCheading[i].ndc === ndcParam) {
          setIndirectCurrentTabIndex(i);
          setIsDirectMode(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indirectNDCheading, ndcParam]);

  return {
    userModulesRoles,
    directcurrentNDCIndex,
    indirectcurrentNDCIndex,
    forecastingMonthsView,
    isConceptLoading,
    contractDetails,
    isInvoiceSalesLoading,
    conceptDetails,
    directNDCheading,
    indirectNDCheading,
    isConstrainedForecastLoading,
    isConstrainedForecastNotesDataLoading,
    constrainedForecastData,
    isConceptDetailsLoading,
    conceptSearchInput,
    constrainedForecastStatus,
    isConstrainedForecastStatusLoading,
    constrainedForecastPlanningPeriods,
    constrainedForecastPeriod,
    constrainedFOrecastPlanningPeriodLoading,
    isDirectMode,
    forecastBtnLabel,
    handleDirectNDCTabChange,
    handleIndirectNDCTabChange,
    handleGetConceptDetails,
    getInvoiceSalesDetails,
    getUnconstrainedForecast,
    getConstrainedForecast,
    handleOptionChange,
    setIsDirectMode,
    ConstrainedForecastNotesData,
  };
};
